import { useCallback, useState, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { useAppearance } from '../../design-system/providers/appearance';
import { usePrefersReducedMotion } from '../../design-system/hooks/usePrefersReducedMotion';
import { brandLogoSrc } from './brandLogo';

import { CaretDownIcon } from '@phosphor-icons/react/dist/csr/CaretDown';

import { CountBadge } from '../../design-system/primitives/CountBadge/CountBadge';
import type { NavChildItem, NavCount, NavItem } from './shellState';
import { readStoredCollapsedGroups, SIDEBAR_ID, writeStoredCollapsedGroups } from './shellState';
import type { NavGroup } from '../navigation';
import { hrefFor } from '../routing';
import styles from './Sidebar.module.css';

/**
 * The primary navigation.
 *
 * Two decisions carry most of the accessibility here:
 *
 * 1. **The current item is announced, not merely coloured.** `aria-current="page"`
 *    is what a screen reader reads; the accent tint and the leading edge are the
 *    matching visual. Handoff, Accessibility: state is never colour alone.
 *
 * 2. **Nav items are real links.** `<a href>` gives middle-click, copy-link and
 *    the browser's own status bar for free, and `onNavigate` intercepts the
 *    ordinary click for the SPA. A `<button>` styled as a link would take all
 *    three away, and a `<div onClick>` would take the keyboard with them.
 *
 * The active edge is `border-inline-start`, NOT the handoff's
 * `inset 2px 0 0 var(--color-accent)`. The handoff itself specifies
 * `inset -2px 0 0` for RTL — that is a per-direction fork, which the same
 * handoff forbids ("nothing is duplicated per direction"). A logical border
 * mirrors for free and is measured doing so in e2e/shell.spec.ts. See
 * task-18-report.md §6.
 */

export interface SidebarProps {
  items: readonly NavItem[];
  /**
   * The sections, in render order. A section whose every entry was
   * permission-filtered out of `items` renders nothing at all.
   */
  groups: readonly NavGroup[];
  /**
   * Pinned to the bottom, below the sections and outside all of them.
   * "Support" is not a place work happens; it is where a reader goes when
   * the work has stopped, and sitting in the scroll flow between two working
   * sections is what made it read as one.
   */
  footerItems?: readonly NavItem[];
  activePath: string;
  collapsed: boolean;
  /** Intercepts the click for the SPA. Without it the link navigates normally. */
  onNavigate?: (path: string) => void;
  id?: string;
  className?: string;
}

function NavCountView({ count }: { count: NavCount }) {
  if (!Number.isFinite(count.count) || count.count <= 0) return null;

  // Handoff: "accent-300 numerals for informational counts, red badge only when
  // action is overdue". The informational form is deliberately NOT a CountBadge:
  // a chip is the shape that says "act on this", and using it for a queue length
  // spends the alert vocabulary on information.
  if (count.tone === 'alert') {
    return <CountBadge count={count.count} label={count.label} tone="alert" />;
  }

  return (
    <span className={styles['count']}>
      <span aria-hidden="true">{count.count}</span>
      <span className={styles['srOnly']}>{count.label}</span>
    </span>
  );
}

export function Sidebar({
  items,
  groups,
  footerItems = [],
  activePath,
  collapsed,
  onNavigate,
  id,
  className,
}: SidebarProps) {
  const { t } = useTranslation();
  const { theme } = useAppearance();
  const reducedMotion = usePrefersReducedMotion();

  function handleClick(path: string) {
    return (event: MouseEvent<HTMLAnchorElement>) => {
      if (onNavigate === undefined) return;
      // Leave the modified click to the browser: ctrl/cmd-click opens a tab, and
      // an SPA that swallows it is an SPA agents cannot open two cases in.
      if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey) return;
      if (event.button !== 0) return;
      event.preventDefault();
      onNavigate(path);
    };
  }

  function renderChild(child: NavChildItem) {
    const current = child.path === activePath;
    return (
      <li key={child.id}>
        <a
          href={hrefFor(child.path)}
          className={[styles['item'], styles['child']].join(' ')}
          aria-current={current ? 'page' : undefined}
          onClick={handleClick(child.path)}
        >
          <span className={styles['label']}>{child.label}</span>
          {child.count ? <NavCountView count={child.count} /> : null}
        </a>
      </li>
    );
  }

  /**
   * A section is open unless the reader folded it away. The stored value
   * holds the FOLDED ids, never the open ones — see
   * `readStoredCollapsedGroups` for why that asymmetry matters.
   */
  const [collapsedGroups, setCollapsedGroups] = useState<readonly string[]>(() =>
    readStoredCollapsedGroups(),
  );

  const toggleGroup = useCallback((groupId: string) => {
    setCollapsedGroups((current) => {
      const next = current.includes(groupId)
        ? current.filter((id) => id !== groupId)
        : [...current, groupId];
      writeStoredCollapsedGroups(next);
      return next;
    });
  }, []);

  /**
   * One nav row. Lifted out of the list's own `map` when the flat list became
   * four grouped ones plus a footer — three call sites rendering this anchor
   * by copy is how the active state, the fill-weight icon and the count badge
   * drift apart one milestone at a time.
   */
  function renderItem(item: NavItem) {
    const Icon = item.icon;
    const current = item.path === activePath;
    return (
      <li key={item.id}>
        <a
          href={hrefFor(item.path)}
          className={styles['item']}
          aria-current={current ? 'page' : undefined}
          onClick={handleClick(item.path)}
        >
          <span className={styles['icon']} aria-hidden="true">
            {/* Handoff: fill weight only for status dots and the ACTIVE nav item. */}
            <Icon weight={current ? 'fill' : 'regular'} />
          </span>
          <span className={styles['label']}>{item.label}</span>
          {item.count ? <NavCountView count={item.count} /> : null}
        </a>

        {/*
            Second level. Hidden entirely while collapsed rather than
            squeezed into 64px: a 14px label has nowhere to go there, and
            rendering it invisible-but-focusable is the "revealed on hover"
            trap task 22 §6.9 documents. The parent link stays reachable, and
            the topbar's toggle is always available to get the labels back.
          */}
        {item.children && item.children.length > 0 && !collapsed ? (
          <ul className={styles['sublist']}>{item.children.map(renderChild)}</ul>
        ) : null}
      </li>
    );
  }

  return (
    <nav
      id={id}
      aria-label={t('nav.primary')}
      data-testid="sidebar"
      data-collapsed={collapsed}
      className={[styles['sidebar'], className].filter(Boolean).join(' ')}
    >
      {/*
        The brand slot. Handoff: the product lockup is the only branded element
        and never competes with content — which is also why it is not a heading:
        the page title owns <h1>, and a brand that took it would put every screen
        under the same name in a screen reader's heading list.
      */}
      <div className={styles['brand']}>
        {/*
          `alt`, NOT `alt=""` + `aria-hidden`, and that changed with the
          wordmark. While `.brandName` rendered the product name beside it, this
          image was a duplicate of a fact already in the accessible tree and was
          correctly hidden. The name line is gone — the logo carries the
          wordmark itself — so hiding the image now would leave the brand slot
          with no accessible name at all in the one place a screen-reader user
          could learn what this application is.
        */}
        <img className={styles['logo']} src={brandLogoSrc(theme)} alt={t('app.name')} />
        <span className={styles['brandTagline']}>{t('app.tagline')}</span>

        {/*
          The collapsed rail's own mark. `favicon.svg` is the butterfly alone —
          legible at 64px where the wordmark above is not, and not a duplicate
          asset: `.logo` above already carries the accessible name ("FS-Helpline")
          and stays mounted, only visually clipped, while collapsed. Naming this
          image too would announce the product twice to a screen reader for one
          fact, so it takes `alt=""` + `aria-hidden` — decoration, not a second
          label.

          The entrance fade is the one state transition in this task with no
          earlier CSS-only precedent to lean on, so it is the one place
          `usePrefersReducedMotion` overrides the animation directly: jsdom
          cannot evaluate the `@media` block below against an injected
          stylesheet, so the inline override is what makes the reduced-motion
          contract for THIS element provable in a unit test rather than only
          "measured in Chromium" like the sidebar background gif above.

          TWO SOURCES OF TRUTH, AND WHY THEY CANNOT DISAGREE: this inline
          `style.animation` and `reset.css`'s own universal
          `@media (prefers-reduced-motion: reduce)` rule both govern this
          element's motion. They cannot fight, because `reset.css` only ever
          `!important`-sets the DURATION/ITERATION longhands, never the
          `animation` shorthand (`reset.test.tsx`'s own structural guard) —
          so when this override sets `animation: 'none'` there is no
          `animation-name` left for any duration rule to apply to, and when
          it is `undefined` the CSS keyframe below runs exactly as declared,
          with `reset.css` still free to force its duration near zero if the
          browser's own (not this hook's) media evaluation says reduce.
          RESIDUAL LIMITATION, stated plainly rather than left implicit: this
          argument is a cascade proof, not a runtime test — no suite here
          can drive a real, live OS preference change through both
          mechanisms at once and observe them agree, because jsdom does not
          evaluate `@media` against an injected stylesheet at all (see
          `usePrefersReducedMotion.ts`'s own docblock). What IS tested is
          that the shape which could make them disagree (`reset.css` ever
          moving to the shorthand) is guarded against.
        */}
        <img
          className={styles['brandMark']}
          src="/favicon.svg"
          alt=""
          aria-hidden="true"
          style={reducedMotion ? { animation: 'none' } : undefined}
        />
      </div>

      {/*
        THE SECTIONS. While the rail is collapsed to 64px they are dropped
        entirely and every item renders in one flat list: a section heading is
        a word, and there is no room for a word — but the items themselves
        must stay reachable, so this is a change of grouping, never of
        contents. The fold state survives the collapse, so a reader who folds
        "Directory" away, narrows their window and widens it again finds it
        still folded.
      */}
      {collapsed ? (
        <ul className={styles['list']}>{items.map(renderItem)}</ul>
      ) : (
        <div className={styles['groups']}>
          {groups.map((group) => {
            const groupItems = items.filter((item) => item.group === group.id);
            // Every entry in this section was permission-filtered away. A
            // heading over nothing tells the reader a feature exists and is
            // empty, when the truth is that it is not theirs.
            if (groupItems.length === 0) return null;

            const open = !collapsedGroups.includes(group.id);
            const listId = `${id ?? SIDEBAR_ID}-group-${group.id}`;

            return (
              <div key={group.id} className={styles['group']}>
                {/*
                  A real <button> with `aria-expanded`, not a <div> and not a
                  <summary>: `aria-expanded` is what a screen reader announces
                  as the state, and `aria-controls` is what ties it to the list
                  it opens. `<details>` gives both for free but takes its
                  marker and its open/close behaviour with it.
                */}
                <button
                  type="button"
                  className={styles['groupHeader']}
                  aria-expanded={open}
                  aria-controls={listId}
                  onClick={() => {
                    toggleGroup(group.id);
                  }}
                >
                  <span className={styles['groupLabel']}>{t(group.labelKey)}</span>
                  <span className={styles['groupCaret']} aria-hidden="true" data-open={open}>
                    <CaretDownIcon size={12} weight="bold" />
                  </span>
                </button>

                {/*
                  Unmounted when folded, never merely hidden: a list left in
                  the document is a list the keyboard still tabs through, and
                  a folded section whose links are still focusable is the
                  "revealed on hover" trap with the mouse taken away.
                */}
                {open ? (
                  <ul id={listId} className={styles['list']}>
                    {groupItems.map(renderItem)}
                  </ul>
                ) : null}
              </div>
            );
          })}
        </div>
      )}

      {/*
        The footer. `margin-block-start: auto` in the stylesheet pins it to
        the bottom of the rail however short the sections above are, and it
        sits OUTSIDE every section so it can never be folded away with one —
        being always in the same place is the whole point of it being here.
      */}
      {footerItems.length === 0 ? null : (
        <ul className={[styles['list'], styles['footer']].join(' ')}>
          {footerItems.map(renderItem)}
        </ul>
      )}
    </nav>
  );
}
